import React, { useMemo } from 'react';
import { useTheme } from '@/hooks/theme-provider'; // 1. Import the useTheme hook
import { Edit } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const colors = {
  'pastel-blue': '#C5D3E8',
  'pastel-cyan': '#A6D6D6',
  'pastel-lavendar': '#C0C9EE',
  'pastel-tan': '#D5C7A3',
  'pastel-green': '#A2AF9B',
  'pastel-pink': '#f9c7cf',
  'pastel-cream': '#ede6dd',
};
const colorKeys = Object.keys(colors);

function NewCourseCard({ course }) {
  const { t } = useTranslation();
  const { theme } = useTheme(); // 2. Get the current theme
  const updatedDate = new Date(course?.updatedAt).toLocaleDateString();

  const consistentColor = useMemo(() => {
    if (!course?.title) return colors[colorKeys[0]];
    let hash = 0;
    for (let i = 0; i < course.title.length; i++) {
      hash += course.title.charCodeAt(i);
    }
    const colorKey = colorKeys[hash % colorKeys.length];
    return colors[colorKey];
  }, [course?.title]);

  return (
    <TooltipProvider>
      <div
        className="rounded-3xl shadow-md p-1 text-left dark:bg-muted"
        // 3. Conditionally apply the background color only for the light theme
        style={theme === 'light' ? { backgroundColor: consistentColor } : {}}>
        <Link to={`/courses/${course?.title}?id=${course?._id}`}>
          <div className="rounded-3xl shadow-md bg-pastel-white dark:bg-black p-4 space-y-2">
            <div className="flex items-center">
              {course?.members?.slice(0, 5).map((avatar, index) => (
                <img
                  key={index}
                  src={avatar.imageUrl}
                  alt="User avatar"
                  className="size-8 rounded-full border-2 border-muted"
                  style={{ marginLeft: index === 0 ? 0 : '-10px', zIndex: 5 - index }}
                />
              ))}
              {course?.members?.length > 3 && (
                <span className="text-sm text-muted-foreground ml-2">
                  {t('courseCard.more', { count: course?.members?.length - 3 })}
                </span>
              )}
            </div>
            {/* Course title */}
            <Tooltip>
              <TooltipTrigger asChild>
                <h2 className="text-lg font-semibold truncate cursor-pointer">{course?.title}</h2>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="break-words">{course?.title}</p>
              </TooltipContent>
            </Tooltip>
            {/* Author */}
            <div className="flex items-center gap-2 text-sm">
              <Edit size={16} className="flex-shrink-0" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate cursor-pointer">
                    {t('courseCard.curatedBy', { author: course?.author })}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="break-words">{t('courseCard.curatedBy', { author: course?.author })}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </Link>
        {/* Updated Date */}
        <div className="flex items-center gap-2 font-semibold text-sm p-2 justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate cursor-pointer">{t('courseCard.updated', { date: updatedDate })}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="break-words">{t('courseCard.updated', { date: updatedDate })}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default NewCourseCard;
